/**
 * PlanCreatedAnnotation
 *
 * Renders a visible plan result card when the message annotation contains
 * show_plan (plan created/updated by tool). Whole card opens the plan in Canvas.
 */

'use client'

import React from 'react'
import { IconListCheck } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

import { PLAN_AWAITING_MARKDOWN_FOOTER } from '@/lib/chat/propose-plan'
import { notifyCrewApprovalsRefresh } from '@/lib/crew/crew-approval-cards-client'

export interface PlanCreatedAnnotationProps {
  annotation: Record<string, unknown>
  chatId?: string | null
  className?: string
}

export function PlanCreatedAnnotation({
  annotation,
  chatId,
  className
}: PlanCreatedAnnotationProps) {
  const { t } = useTranslation()

  const plan = React.useMemo(() => {
    const raw = annotation.show_plan
    if (!raw || typeof raw !== 'object' || !('planId' in raw)) return null
    const o = raw as { planId?: string; content?: string; status?: string }
    if (typeof o.planId !== 'string') return null
    return {
      planId: o.planId,
      content: typeof o.content === 'string' ? o.content : '',
      status: typeof o.status === 'string' ? o.status : undefined
    }
  }, [annotation])

  React.useEffect(() => {
    if (!plan) return
    const awaiting =
      plan.status === 'awaiting_approval' ||
      plan.content.includes(PLAN_AWAITING_MARKDOWN_FOOTER)
    if (awaiting) notifyCrewApprovalsRefresh()
  }, [plan])

  if (!plan) return null

  const openInCanvas = () => {
    window.dispatchEvent(
      new CustomEvent('openPlanInCanvas', {
        detail: {
          planId: plan.planId,
          content: plan.content,
          ...(plan.status ? { status: plan.status } : {})
        }
      })
    )
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={openInCanvas}
        className="border-border/50 bg-muted/30 hover:bg-muted/50 focus-visible:ring-ring flex w-full cursor-pointer items-center gap-3 rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2"
      >
        <IconListCheck className="text-muted-foreground size-5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">
            {t('Losi Execution Plan')}
          </div>
          <div className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
            {plan.content?.trim().slice(0, 120)}
            {(plan.content?.length ?? 0) > 120 ? '…' : ''}
          </div>
        </div>
        <span className="text-primary shrink-0 text-sm font-medium">
          {t('Open in Canvas')}
        </span>
      </button>
    </div>
  )
}
